import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdsBanner, { BannerPosition } from "@components/ads-banner/ads-banner";
import LessonComplete from "@components/lesson-complete/lesson-complete";
import WrapperAdsBanner from "@components/wrapper-ads-banner/WrapperAdsBanner";
import WrapperRewardsModals from "@components/wrapper-rewards-modals/wrapper-rewards-modals";

import { useAppDispatch } from "@hooks/use-store";
import { setIsAdsScriptsReloaded } from "@store/app/app.slice";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import {
  getStoredRewardIds,
  rewardsModalContent,
  updateStoredRewardIds,
} from "@helpers/helpers";
import { ExerciseCompleteData, IReward } from "@typings/global.types";

function WrapperLessonComplete() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [lessonCompleteData, setLessonCompleteData] =
    useState<ExerciseCompleteData | null>(null);
  const [currentModalType, setCurrentModalType] = useState<number | null>(null);

  useEffect(() => {
    const sessionStorageData = window.sessionStorage.getItem(
      "exerciseCompleteData",
    );
    if (sessionStorageData && JSON.parse(sessionStorageData).lastLesson) {
      setLessonCompleteData(JSON.parse(sessionStorageData));
    } else {
      navigate(formatUrlWithLocale("/typing-tutor/"));
    }
  }, [navigate]);

  useEffect(() => {
    dispatch(setIsAdsScriptsReloaded(true));
  }, [dispatch]);

  useEffect(() => {
    if (lessonCompleteData) {
      const storedRewardIds = getStoredRewardIds(window.apiUserId);
      const currentRewardIds = lessonCompleteData.rewards.map(
        (reward: IReward) => reward.id,
      );

      if (!storedRewardIds) {
        updateStoredRewardIds(currentRewardIds, window.apiUserId);
        return;
      }

      const newRewardIds = currentRewardIds.filter(
        (id) => !storedRewardIds.includes(id) && [4].includes(id),
      );

      if (newRewardIds.length > 0) {
        updateStoredRewardIds(
          [...storedRewardIds, ...newRewardIds],
          window.apiUserId,
        );
      }

      const validRewardIds = newRewardIds.filter(
        (id) => rewardsModalContent[id as keyof typeof rewardsModalContent],
      );

      if (validRewardIds.length > 0) {
        const timeoutId = setTimeout(() => {
          setCurrentModalType(validRewardIds[0]);
        }, 500);
        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeoutId);
      }
    }
  }, [lessonCompleteData]);

  const handleCloseRewardModal = () => {
    setCurrentModalType(null);
  };

  if (!lessonCompleteData?.lastLesson) {
    return null;
  }

  return (
    <>
      <div className='flex flex-col-reverse md:flex-col'>
        <div className='mb-2 min-h-[110px] md:mb-2'>
          <WrapperAdsBanner page='result'>
            <AdsBanner bannerPosition={BannerPosition.RESULT} />
          </WrapperAdsBanner>
        </div>
        <LessonComplete
          lastLesson={lessonCompleteData.lastLesson}
          coins={lessonCompleteData.coins}
          lessonCoins={lessonCompleteData.lessonCoins}
          nextExerciseId={lessonCompleteData.nextExerciseId}
          firstExerciseId={lessonCompleteData.exercises[0].id}
          gameMode={lessonCompleteData.gameMode}
        />
      </div>

      <WrapperRewardsModals
        closeModalHandler={handleCloseRewardModal}
        modalType={currentModalType}
        queueModal={0}
      />
    </>
  );
}

export default WrapperLessonComplete;
